import { useIsUserHcpVerificationEnabled } from '@/features/flags/hcp-verification-flags';
import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { hcpVerificationReminderEligibleAtom } from './hcp-verification-atoms';
import { useHcpVerificationStatus } from './hcp-verification-state';
import { useSyncHcpVerificationFromInmails } from './use-sync-hcp-verification-from-inmails';

const HcpVerificationBadgeProbe = () => {
  const { state } = useHcpVerificationStatus();
  useSyncHcpVerificationFromInmails();

  const setReminderEligible = useSetAtom(hcpVerificationReminderEligibleAtom);
  const isReminderEligible = state === 'unverified' || state === 'rejected';
  useEffect(() => {
    setReminderEligible(isReminderEligible);
    return () => setReminderEligible(false);
  }, [isReminderEligible, setReminderEligible]);

  return null;
};

/**
 * Headless, flag-gated badge probe mounted with the universal modals.
 *
 * The backend writes the daily "verify your clinician profile" inmail as a
 * side effect of the first eligible `GET /hcp-verifications:status` of the UTC
 * day, so that read has to happen on app load — not only when the user opens
 * Account settings. This component issues it and renders nothing; the inmail
 * then arrives through the ordinary notifications query like any other row.
 *
 * A later grant or refuse writes another inmail. The probe watches that same
 * notifications query and invalidates the status cache when a grant or refuse
 * that was not yet in the bell when the status last settled disagrees with
 * it, so a stale cache cannot desync from the bell — and a refuse older than
 * the user's own resubmission cannot make the status poll. Daily nags are
 * ignored.
 *
 * The same read feeds the weekly reminder: the probe publishes
 * `hcpVerificationReminderEligibleAtom` (`unverified` / `rejected`) for
 * `launchModalsAtom` and resets it on unmount, so a flag flip or sign-out
 * drops the row.
 *
 * Reads only the flag leaf before subscribing so a flag-OFF build never
 * touches the query or the user gate on its account.
 */
export const HcpVerificationNotificationSource = () => {
  const isEnabled = useIsUserHcpVerificationEnabled();
  if (!isEnabled) return null;
  return <HcpVerificationBadgeProbe />;
};
